import React, {useEffect, useMemo, useRef, useState} from "react";
import type {TFunction} from "i18next";
import {useRouter} from "next/router";
import {useTranslation} from "next-i18next";
import {GoodsItem, GoodsSelection} from "../../interfaces/GoodsInterface";
import {calculateGoodsTotal, calculateGoodsVatAmount, calculateSelectedGoodsTotal} from "../../helpers/GoodsPricing";
import IconArrowDown from "../../data/Icons/leon-inmobiliaria-icons-arrow-1-down.svg";
import {calendarDateToIsoTimestamp} from "../../helpers/CalendarDate";

const GOODS_CART_STORAGE_KEY = "leon.goods.cart.v1";
const GOODS_CART_EVENT = "leon:goods-cart-updated";

interface GoodsSelectorProps {
	defaultDateFrom?: string;
	defaultDateTo?: string;
	showBookingIdApply?: boolean;
	onChange?: (goods: GoodsSelection[], total: number) => void;
}

function formatPrice(value: number, locale?: string) {
	return new Intl.NumberFormat(locale ?? "de", {
		style: "currency",
		currency: "EUR"
	}).format(value);
}

function getGoodsCategory(goods: GoodsItem) {
	return goods.category_title?.trim() || "";
}

function getPrimaryGoodsImage(goods: GoodsItem) {
	return goods.images?.find((image) => image.is_main)?.image_url || goods.images?.[0]?.image_url || goods.image_url || "";
}

function getGoodsImages(goods: GoodsItem) {
	const imageUrls = [
		...(goods.images ?? []).map((image) => image.image_url),
		goods.image_url || ""
	].filter((imageUrl): imageUrl is string => Boolean(imageUrl));

	return Array.from(new Set(imageUrls));
}

function getGoodsBillingSuffix(goods: GoodsItem) {
	if (goods.billing_type === "fixed") return "";
	return `/${goods.billing_type.replace("per_", "")}`;
}

function hasCompleteDateRange(selection: GoodsSelection) {
	return Boolean(selection.date_from && selection.date_to);
}

function sanitizeCartSelections(value: unknown): GoodsSelection[] {
	if (!Array.isArray(value)) return [];

	return value
		.map((item) => {
			if (!item || typeof item !== "object") return null;
			const selection = item as Partial<GoodsSelection>;
			const goodsId = Number(selection.goods_id);
			const quantity = Number(selection.quantity);

			if (!Number.isFinite(goodsId) || goodsId <= 0) return null;

			return normalizeSelectionDateRange({
				goods_id: goodsId,
				quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
				date_from: selection.date_from || undefined,
				date_to: selection.date_to || undefined,
				time_from: selection.time_from || undefined,
				time_to: selection.time_to || undefined
			});
		})
		.filter((item): item is GoodsSelection => Boolean(item));
}

function readStoredGoodsCart() {
	if (typeof window === "undefined") return [];

	try {
		return sanitizeCartSelections(JSON.parse(window.localStorage.getItem(GOODS_CART_STORAGE_KEY) || "[]"));
	} catch (error) {
		return [];
	}
}

function writeStoredGoodsCart(selections: GoodsSelection[]) {
	if (typeof window === "undefined") return;

	window.localStorage.setItem(GOODS_CART_STORAGE_KEY, JSON.stringify(selections));
	window.setTimeout(() => window.dispatchEvent(new Event(GOODS_CART_EVENT)), 0);
}

function useGoodsCart(defaultDateFrom?: string, defaultDateTo?: string) {
	const [selectedGoods, setSelectedGoodsState] = useState<GoodsSelection[]>([]);

	useEffect(() => {
		setSelectedGoodsState(readStoredGoodsCart());

		function handleCartUpdate() {
			setSelectedGoodsState(readStoredGoodsCart());
		}

		function handleStorageUpdate(event: StorageEvent) {
			if (event.key !== GOODS_CART_STORAGE_KEY) return;
			setSelectedGoodsState(readStoredGoodsCart());
		}

		window.addEventListener(GOODS_CART_EVENT, handleCartUpdate);
		window.addEventListener("storage", handleStorageUpdate);

		return () => {
			window.removeEventListener(GOODS_CART_EVENT, handleCartUpdate);
			window.removeEventListener("storage", handleStorageUpdate);
		};
	}, []);

	useEffect(() => {
		if (!defaultDateFrom && !defaultDateTo) return;

		setSelectedGoodsState((current) => {
			const next = current.map((selection) => constrainSelectionDateRange({
				...selection,
				date_from: selection.date_from || defaultDateFrom || undefined,
				date_to: selection.date_to || defaultDateTo || undefined
			}, defaultDateFrom, defaultDateTo));

			const changed = JSON.stringify(next) !== JSON.stringify(current);
			if (changed) writeStoredGoodsCart(next);

			return changed ? next : current;
		});
	}, [defaultDateFrom, defaultDateTo]);

	function setSelectedGoods(updater: React.SetStateAction<GoodsSelection[]>) {
		setSelectedGoodsState((current) => {
			const next = sanitizeCartSelections(typeof updater === "function" ? (updater as (value: GoodsSelection[]) => GoodsSelection[])(current) : updater);
			writeStoredGoodsCart(next);
			return next;
		});
	}

	return [selectedGoods, setSelectedGoods] as const;
}

export default function GoodsSelector({defaultDateFrom, defaultDateTo, onChange, showBookingIdApply}: GoodsSelectorProps) {
	const router = useRouter();
	const {t} = useTranslation();
	const [goodsItems, setGoodsItems] = useState<GoodsItem[]>([]);
	const [selectedGoods, setSelectedGoods] = useGoodsCart(defaultDateFrom, defaultDateTo);
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [referenceId, setReferenceId] = useState("");
	const [isLoadingGoods, setIsLoadingGoods] = useState(true);
	const [isApplying, setIsApplying] = useState(false);
	const [message, setMessage] = useState("");
	const [isCartOpen, setIsCartOpen] = useState(false);
	const [addedGoodsTitle, setAddedGoodsTitle] = useState("");
	const addedGoodsTimer = useRef<number | null>(null);

	useEffect(() => {
		let isCancelled = false;
		setIsLoadingGoods(true);

		fetch("/api/goods", {
			headers: router.locale ? {"Accept-Language": router.locale} : undefined
		})
			.then((response) => response.json())
			.then((items) => {
				if (!isCancelled) {
					setGoodsItems(items);
				}
			})
			.catch(() => {
				if (!isCancelled) {
					setGoodsItems([]);
				}
			})
			.finally(() => {
				if (!isCancelled) {
					setIsLoadingGoods(false);
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [router.locale]);

	useEffect(() => {
		const total = calculateSelectedGoodsTotal(goodsItems, selectedGoods);
		onChange?.(selectedGoods, total);
	}, [goodsItems, selectedGoods, onChange]);

	useEffect(() => {
		if (!router.isReady || !showBookingIdApply || !router.query.referenceId) {
			return;
		}

		setReferenceId(Array.isArray(router.query.referenceId) ? router.query.referenceId[0] : router.query.referenceId);
	}, [router.isReady, router.query.referenceId, showBookingIdApply]);

	useEffect(() => {
		return () => {
			if (addedGoodsTimer.current) {
				window.clearTimeout(addedGoodsTimer.current);
			}
		};
	}, []);

	const filteredGoods = useMemo(() => {
		const term = search.trim().toLowerCase();
		return goodsItems.filter((goods) => {
			const goodsCategory = getGoodsCategory(goods);
			const matchesCategory = !categoryFilter || goodsCategory === categoryFilter;
			const matchesSearch = !term || [goods.title, goods.short_description, goods.description, goods.match_code, goods.category_title]
				.filter(Boolean)
				.some((value) => value!.toLowerCase().includes(term));
			return matchesCategory && matchesSearch;
		});
	}, [categoryFilter, goodsItems, search]);

	const categories = useMemo(() => {
		return Array.from(new Set(goodsItems
			.map((goods) => getGoodsCategory(goods))
			.filter((category): category is string => Boolean(category))
		)).sort((first, second) => first.localeCompare(second));
	}, [goodsItems]);

	const selectedRows = selectedGoods
		.map((selection) => ({
			selection,
			goods: goodsItems.find((goods) => goods.id === selection.goods_id)
		}))
		.filter((row): row is { selection: GoodsSelection; goods: GoodsItem } => Boolean(row.goods));
	const total = calculateSelectedGoodsTotal(goodsItems, selectedGoods);

	function addGoods(goods: GoodsItem) {
		setMessage("");
		setAddedGoodsTitle(goods.title);
		if (addedGoodsTimer.current) {
			window.clearTimeout(addedGoodsTimer.current);
		}
		addedGoodsTimer.current = window.setTimeout(() => {
			setAddedGoodsTitle("");
			addedGoodsTimer.current = null;
		}, 5000);
		setSelectedGoods((current) => {
			const existing = current.find((selection) => selection.goods_id === goods.id);
			if (existing) {
				return current.map((selection) => {
					if (selection.goods_id !== goods.id) return selection;
					const maxQuantity = goods.max_quantity ?? goods.stock_quantity ?? 99;
					return {...selection, quantity: Math.min(maxQuantity, selection.quantity + 1)};
				});
			}
			return [
				...current,
				{
					goods_id: goods.id,
					quantity: 1,
					date_from: defaultDateFrom || undefined,
					date_to: defaultDateTo || undefined
				}
			];
		});
	}

	function updateSelection(goodsId: number, data: Partial<GoodsSelection>) {
		setSelectedGoods((current) => current.map((selection) => (
			selection.goods_id === goodsId ? constrainSelectionDateRange({...selection, ...data}, defaultDateFrom, defaultDateTo) : selection
		)));
	}

	function updateQuantity(goods: GoodsItem, quantity: number) {
		const maxQuantity = goods.max_quantity ?? goods.stock_quantity ?? 99;
		const safeQuantity = Math.max(1, Math.min(maxQuantity, quantity));
		updateSelection(goods.id, {quantity: safeQuantity});
	}

	function removeGoods(goodsId: number) {
		setSelectedGoods((current) => current.filter((item) => item.goods_id !== goodsId));
	}

	async function applyToBooking() {
		setMessage("");
		if (!referenceId || selectedGoods.length === 0) {
			setMessage(t("components.goodsSelector.messages.enterReferenceAndGoods"));
			return;
		}
		if (selectedGoods.some((selection) => !hasCompleteDateRange(selection))) {
			setMessage(t("components.goodsSelector.messages.chooseDateRange"));
			return;
		}
		setIsApplying(true);
		try {
			const submittedGoods = selectedGoods.map((selection) => ({
				...selection,
				date_from: calendarDateToIsoTimestamp(selection.date_from),
				date_to: calendarDateToIsoTimestamp(selection.date_to)
			}));
			const response = await fetch("/api/booking-goods", {
				method: "POST",
				headers: {"Content-Type": "application/json"},
				body: JSON.stringify({referenceId, goods: submittedGoods})
			});
			const data = await response.json();
			if (!response.ok) {
				setMessage(data.messageKey ? t(data.messageKey) : t("components.goodsSelector.messages.couldNotAdd"));
				return;
			}
			setSelectedGoods([]);
			setMessage(data.target === "request" ? t("components.goodsSelector.messages.addedToRequest") : t("components.goodsSelector.messages.addedToBooking"));
		} catch (error) {
			setMessage(t("components.goodsSelector.messages.couldNotAdd"));
		} finally {
			setIsApplying(false);
		}
	}

	return (
		<div className="space-y-8">
			<div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_260px]">
				<input
					className="border border-leo-divider-gray-2 px-4 py-3 text-sm"
					value={search}
					onChange={(event) => setSearch(event.target.value)}
					placeholder={t("components.goodsSelector.searchGoods")}
				/>
				<select
					className="border border-leo-divider-gray-2 px-4 py-3 text-sm"
					value={categoryFilter}
					onChange={(event) => setCategoryFilter(event.target.value)}
				>
					<option value="">{t("components.goodsSelector.allCategories")}</option>
					{categories.map((category) => (
						<option key={category} value={category}>{category}</option>
					))}
				</select>
			</div>

			{isLoadingGoods ? (
				<div className="flex w-full flex-wrap justify-center gap-4 md:justify-between" aria-live="polite" aria-label={t("components.goodsSelector.loadingGoods")}>
					{[1, 2, 3].map((item) => (
						<div key={item} className="m-1 mb-4 flex h-[610px] w-full animate-pulse flex-col rounded-[20px] bg-white p-2 pb-8 shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] sm:max-w-[500px] md:w-[345px] lg:h-[620px] lg:min-w-[400px] lg:max-w-[480px] lg:flex-1 lg:p-[15px]">
							<div className="h-[260px] w-full rounded-[16px] bg-leo-divider-gray-2 lg:h-[265px]"/>
							<div className="flex grow flex-col px-2 pt-6 lg:px-4">
								<div className="h-4 w-28 bg-leo-divider-gray-2"/>
								<div className="mt-4 h-8 w-3/4 bg-leo-divider-gray-2"/>
								<div className="mt-5 space-y-2">
									<div className="h-3 w-full bg-leo-divider-gray-2"/>
									<div className="h-3 w-5/6 bg-leo-divider-gray-2"/>
									<div className="h-3 w-2/3 bg-leo-divider-gray-2"/>
								</div>
								<div className="mt-auto h-7 w-36 bg-leo-divider-gray-2"/>
							</div>
							<div className="h-12 w-full max-w-[200px] self-end rounded-[10px] bg-leo-divider-gray-2"/>
						</div>
					))}
				</div>
			) : (
				<div className="flex w-full flex-wrap items-stretch justify-center gap-4 md:justify-between">
					{filteredGoods.map((goods) => (
						<GoodsCard key={goods.id} goods={goods} locale={router.locale} t={t} onAdd={addGoods}/>
					))}
				</div>
			)}

			<div className="border border-leo-divider-gray-2 bg-white p-5">
				<h3 className="text-xl font-bold text-leo-dark-brown">{t("components.goodsSelector.selectedGoods")}</h3>
				{selectedRows.length === 0 ? (
					<p className="mt-4 text-sm text-leo-divider-gray">{t("components.goodsSelector.noGoodsSelected")}</p>
				) : (
					<SelectedGoodsTable
						rows={selectedRows}
						locale={router.locale}
						t={t}
						minDate={defaultDateFrom}
						maxDate={defaultDateTo}
						onQuantityChange={updateQuantity}
						onSelectionChange={updateSelection}
						onRemove={removeGoods}
					/>
				)}
				<div className="mt-5 flex flex-col gap-4 border-t border-leo-divider-gray-2 pt-5 md:flex-row md:items-center md:justify-between">
					<p className="text-lg font-bold">{t("components.goodsSelector.goodsTotal")}: {formatPrice(total, router.locale)}</p>
					{showBookingIdApply && (
						<div className="flex flex-col gap-3 sm:flex-row">
							<input
								className="border border-leo-divider-gray-2 px-4 py-3 text-sm"
								value={referenceId}
								onChange={(event) => setReferenceId(event.target.value)}
								placeholder={t("components.goodsSelector.requestOrBookingId")}
							/>
							<button type="button" disabled={isApplying} className="bg-[#43c4f7] px-5 py-3 text-sm font-bold uppercase text-white disabled:opacity-60" onClick={applyToBooking}>
								{t("components.goodsSelector.applyGoods")}
							</button>
						</div>
					)}
				</div>
				{message && <p className="mt-4 text-sm font-bold text-[#bb2124]">{message}</p>}
			</div>

			<button
				type="button"
				aria-label={t("components.goodsSelector.openGoodsCart")}
				className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-leo-dark-brown text-white shadow-xl"
				onClick={() => setIsCartOpen(true)}
			>
				<svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
					<path d="M6 8h12l-1.1 11H7.1L6 8Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
					<path d="M9 8a3 3 0 0 1 6 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
					<path d="M9 12v3M15 12v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
				</svg>
				{selectedGoods.length > 0 && (
					<span className="absolute -right-1 -top-1 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-[#43c4f7] px-1 text-xs font-bold text-white">
						{selectedGoods.reduce((sum, item) => sum + item.quantity, 0)}
					</span>
				)}
			</button>

			{addedGoodsTitle && (
				<div className="fixed bottom-24 right-6 z-50 w-[calc(100%-3rem)] max-w-sm rounded-[10px] bg-white p-4 text-leo-dark-brown shadow-[0px_1px_12px_rgba(0,0,0,0.25)]" role="status" aria-live="polite">
					<p className="text-base font-bold">{t("components.goodsSelector.messages.addedToCartTitle")}</p>
					<p className="mt-1 text-sm">{t("components.goodsSelector.messages.addedToCart", {title: addedGoodsTitle})}</p>
				</div>
			)}

			{isCartOpen && (
				<div className="fixed inset-0 z-50 bg-black/50 px-4 py-6" role="dialog" aria-modal="true">
					<div className="mx-auto flex max-h-full max-w-6xl flex-col bg-white mt-[100px]">
						<div className="flex items-center justify-between border-b border-leo-divider-gray-2 p-5">
							<div>
								<h3 className="text-xl font-bold text-leo-dark-brown">{t("components.goodsSelector.goodsCart")}</h3>
								<p className="mt-1 text-sm text-leo-divider-gray">{t("components.goodsSelector.cartDescription")}</p>
							</div>
							<button type="button" className="px-3 py-2 text-2xl leading-none text-leo-dark-brown" aria-label={t("components.goodsSelector.closeGoodsCart")} onClick={() => setIsCartOpen(false)}>x</button>
						</div>
						<div className="overflow-y-auto p-5">
							{selectedRows.length === 0 ? (
								<p className="text-sm text-leo-divider-gray">{t("components.goodsSelector.noGoodsSelected")}</p>
							) : (
								<SelectedGoodsTable
									rows={selectedRows}
									locale={router.locale}
									t={t}
									minDate={defaultDateFrom}
									maxDate={defaultDateTo}
									onQuantityChange={updateQuantity}
									onSelectionChange={updateSelection}
									onRemove={removeGoods}
									compact
								/>
							)}
						</div>
						<div className="flex flex-col gap-3 border-t border-leo-divider-gray-2 p-5 sm:flex-row sm:items-center sm:justify-between">
							<p className="text-lg font-bold">{t("components.goodsSelector.goodsTotal")}: {formatPrice(total, router.locale)}</p>
							<button type="button" className="bg-leo-dark-brown px-5 py-3 text-sm font-bold uppercase text-white" onClick={() => setIsCartOpen(false)}>
								{t("components.goodsSelector.continue")}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

interface SelectedGoodsTableProps {
	rows: { selection: GoodsSelection; goods: GoodsItem }[];
	locale?: string;
	compact?: boolean;
	t: TFunction;
	minDate?: string;
	maxDate?: string;
	onQuantityChange: (goods: GoodsItem, quantity: number) => void;
	onSelectionChange: (goodsId: number, data: Partial<GoodsSelection>) => void;
	onRemove: (goodsId: number) => void;
}

interface GoodsCardProps {
	goods: GoodsItem;
	locale?: string;
	t: TFunction;
	onAdd: (goods: GoodsItem) => void;
}

function GoodsCard({goods, locale, t, onAdd}: GoodsCardProps) {
	const [currentImage, setCurrentImage] = useState(0);
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
	const [canExpandDescription, setCanExpandDescription] = useState(false);
	const descriptionRef = useRef<HTMLParagraphElement>(null);
	const imageUrls = getGoodsImages(goods);
	const hasMultipleImages = imageUrls.length > 1;
	const visibleImage = imageUrls[currentImage] || "";

	useEffect(() => {
		const description = descriptionRef.current;
		if (!description) {
			setCanExpandDescription(false);
			return;
		}

		const updateCanExpand = () => {
			const lineHeight = Number.parseFloat(window.getComputedStyle(description).lineHeight);
			const isOverflowing = Number.isFinite(lineHeight) && description.scrollHeight > lineHeight * 4 + 1;
			setCanExpandDescription(isOverflowing);
			if (!isOverflowing) setIsDescriptionExpanded(false);
		};

		updateCanExpand();
		const resizeObserver = new ResizeObserver(updateCanExpand);
		resizeObserver.observe(description);

		return () => resizeObserver.disconnect();
	}, [goods.description]);

	function showPreviousImage(event: React.MouseEvent<HTMLButtonElement>) {
		event.preventDefault();
		event.stopPropagation();
		if (!hasMultipleImages) return;
		setCurrentImage((index) => index === 0 ? imageUrls.length - 1 : index - 1);
	}

	function showNextImage(event: React.MouseEvent<HTMLButtonElement>) {
		event.preventDefault();
		event.stopPropagation();
		if (!hasMultipleImages) return;
		setCurrentImage((index) => index + 1 < imageUrls.length ? index + 1 : 0);
	}

	return (
		<article className="m-1 mb-4 flex h-auto min-h-[610px] w-full flex-col rounded-[20px] bg-white p-2 pb-8 shadow-[0px_1px_6px_rgba(0,0,0,0.1607843137)] sm:max-w-[500px] md:w-[345px] lg:min-h-[620px] lg:min-w-[400px] lg:max-w-[480px] lg:flex-1 lg:p-[15px]">
			<div className="relative flex h-[260px] w-full items-center justify-center overflow-hidden rounded-[16px] bg-white lg:h-[265px]">
				{visibleImage ? (
					<img className="h-full w-full object-contain" src={visibleImage} alt={goods.title}/>
				) : (
					<span className="px-4 text-center text-sm text-leo-divider-gray">{t("components.goodsSelector.table.noImage")}</span>
				)}
				{hasMultipleImages && (
					<>
						<button
							type="button"
							className="absolute left-0 top-1/2 z-10 flex h-14 w-14 -translate-y-1/2 rotate-90 items-center justify-center p-2 text-white opacity-80 backdrop-brightness-75 transition-opacity hover:opacity-95 lg:h-10 lg:w-10 lg:opacity-60"
							aria-label={t("components.goodsSelector.previousImage")}
							onClick={showPreviousImage}
						>
							<IconArrowDown/>
						</button>
						<button
							type="button"
							className="absolute right-0 top-1/2 z-10 flex h-14 w-14 -translate-y-1/2 -rotate-90 items-center justify-center p-2 text-white opacity-80 backdrop-brightness-75 transition-opacity hover:opacity-95 lg:h-10 lg:w-10 lg:opacity-60"
							aria-label={t("components.goodsSelector.nextImage")}
							onClick={showNextImage}
						>
							<IconArrowDown/>
						</button>
						<div className="hidden absolute bottom-3 right-3 rounded bg-black/60 px-3 py-1 text-xs font-bold text-white">
							{currentImage + 1}/{imageUrls.length}
						</div>
					</>
				)}
			</div>
			{hasMultipleImages && (
				<div
					className="h-1 w-full"
					style={{
						background: `linear-gradient(90deg, #F1F1F1 ${(100 / imageUrls.length) * currentImage}%,#43c4f7 0 ${(100 / imageUrls.length) * (currentImage + 1)}%, #F1F1F1 ${(100 / imageUrls.length) * (currentImage + 1)}%)`,
					}}
				/>
			)}
			<div className="flex grow flex-col px-2 pt-6 lg:px-4">
				<div className="min-w-0 grow">
					{getGoodsCategory(goods) && <p className="text-xs font-bold uppercase tracking-normal text-leo-blue">{getGoodsCategory(goods)}</p>}
					<h3 className="mt-2 text-[22px] font-bold leading-[1.15] text-leo-dark-brown sm:text-[30px]">{goods.title}</h3>
					{goods.short_description && <p className="mt-4 line-clamp-2 text-[13px] leading-[1.35] text-leo-dark-brown sm:text-[14px]">{goods.short_description}</p>}
					{goods.description && (
						<div className="mt-3">
							<p ref={descriptionRef} className={`${isDescriptionExpanded ? "" : "line-clamp-4"} whitespace-pre-line text-[12px] leading-[1.35] text-leo-dark-brown sm:text-[14px]`}>
								{goods.description}
							</p>
							{canExpandDescription && (
								<button
									type="button"
									className="mt-2 text-sm font-bold text-leo-blue underline"
									onClick={() => setIsDescriptionExpanded((expanded) => !expanded)}
									aria-expanded={isDescriptionExpanded}
								>
									{t(isDescriptionExpanded ? "components.goodsSelector.showLess" : "components.goodsSelector.readMore")}
								</button>
							)}
						</div>
					)}
				</div>
				{hasMultipleImages && (
					<div className="mt-4 flex gap-2 overflow-x-auto pb-1 hidden">
						{imageUrls.slice(0, 4).map((imageUrl, index) => (
							<button
								key={imageUrl}
								type="button"
								className={`h-12 w-16 shrink-0 overflow-hidden rounded border object-cover ${index === currentImage ? "border-leo-blue" : "border-leo-divider-gray-2"}`}
								aria-label={t("components.goodsSelector.selectImage", {number: index + 1})}
								onClick={() => setCurrentImage(index)}
							>
								<img className="h-full w-full object-contain" src={imageUrl} alt={goods.title}/>
							</button>
						))}
					</div>
				)}
				<div className="mt-5 flex flex-col gap-3 border-t border-leo-divider-gray-2 pt-4">
					<div>
						<p className="font-bold text-leo-blue">{formatPrice(goods.price_gross, locale)} {getGoodsBillingSuffix(goods)}</p>
						{typeof goods.tax_rate === "number" && (
							<p className="mt-1 text-xs text-leo-divider-gray">{t("components.goodsSelector.vatIncluded", {rate: goods.tax_rate.toLocaleString(locale)})}</p>
						)}
					</div>
					{(goods.max_quantity || goods.stock_quantity) && (
						<p className="text-xs text-leo-divider-gray">
							{t("components.goodsSelector.table.quantity")}: {goods.max_quantity ?? goods.stock_quantity}
						</p>
					)}
				</div>
			</div>
			<button
				type="button"
				className="mt-4 flex h-12 w-full max-w-[200px] items-center justify-center self-end rounded-[10px] bg-gradient-to-b from-[#a8e1f7] to-[#43c4f7] px-6 text-center text-sm font-bold uppercase text-white transition-all duration-300 hover:bg-leo-dark-brown hover:from-transparent hover:to-transparent"
				onClick={() => onAdd(goods)}
			>
				{t("components.goodsSelector.add")}
			</button>
		</article>
	);
}

function SelectedGoodsTable({rows, locale, compact, t, minDate, maxDate, onQuantityChange, onSelectionChange, onRemove}: SelectedGoodsTableProps) {
	return (
		<div className="mt-4 overflow-x-auto">
			<table className={`w-full text-sm ${compact ? "min-w-[900px]" : "min-w-[1080px]"}`}>
				<thead>
					<tr className="border-b border-leo-divider-gray-2 text-left">
						<th className="py-3 pr-3">{t("components.goodsSelector.table.image")}</th>
						<th className="py-3">{t("components.goodsSelector.table.goods")}</th>
						<th className="py-3">{t("components.goodsSelector.table.quantity")}</th>
						<th className="py-3">{t("components.goodsSelector.table.from")}</th>
						<th className="py-3">{t("components.goodsSelector.table.to")}</th>
						<th className="py-3">{t("components.goodsSelector.table.timeFrom")}</th>
						<th className="py-3">{t("components.goodsSelector.table.timeTo")}</th>
						<th className="py-3 text-right">{t("components.goodsSelector.table.vat")}</th>
						<th className="py-3 text-right">{t("components.goodsSelector.table.total")}</th>
						<th className="py-3"></th>
					</tr>
				</thead>
				<tbody>
					{rows.map(({selection, goods}) => (
						<tr key={goods.id} className="border-b border-leo-divider-gray-2">
							<td className="py-3 pr-3">
								{getPrimaryGoodsImage(goods) ? (
									<img className="h-14 w-20 object-cover" src={getPrimaryGoodsImage(goods)} alt={goods.title}/>
								) : (
									<div className="flex h-14 w-20 items-center justify-center bg-leo-divider-gray-2 text-xs text-leo-divider-gray">{t("components.goodsSelector.table.noImage")}</div>
								)}
							</td>
							<td className="py-3 pr-3">
								<p className="font-bold text-leo-dark-brown">{goods.title}</p>
								{getGoodsCategory(goods) && <p className="mt-1 text-xs font-bold uppercase text-[#43c4f7]">{getGoodsCategory(goods)}</p>}
							</td>
							<td className="py-3 pr-3">
								<input
									className="w-20 border border-leo-divider-gray-2 px-3 py-2"
									type="number"
									min={1}
									max={goods.max_quantity ?? goods.stock_quantity ?? 99}
									value={selection.quantity}
									onChange={(event) => onQuantityChange(goods, Number(event.target.value))}
								/>
							</td>
							<td className="py-3 pr-3">
								<input
									className="w-36 border border-leo-divider-gray-2 px-3 py-2"
									type="date"
									min={minDate}
									max={selection.date_to || maxDate}
									value={selection.date_from ?? ""}
									onChange={(event) => onSelectionChange(goods.id, {date_from: event.target.value || undefined})}
								/>
							</td>
							<td className="py-3 pr-3">
								<input
									className="w-36 border border-leo-divider-gray-2 px-3 py-2"
									type="date"
									min={selection.date_from || minDate}
									max={maxDate}
									value={selection.date_to ?? ""}
									onChange={(event) => onSelectionChange(goods.id, {date_to: event.target.value || undefined})}
								/>
							</td>
							<td className="py-3 pr-3">
								<input
									className="w-28 border border-leo-divider-gray-2 px-3 py-2"
									type="time"
									value={selection.time_from ?? ""}
									onChange={(event) => onSelectionChange(goods.id, {time_from: event.target.value || undefined})}
								/>
							</td>
							<td className="py-3 pr-3">
								<input
									className="w-28 border border-leo-divider-gray-2 px-3 py-2"
									type="time"
									value={selection.time_to ?? ""}
									onChange={(event) => onSelectionChange(goods.id, {time_to: event.target.value || undefined})}
								/>
							</td>
							<td className="py-3 text-right">
								{typeof goods.tax_rate === "number" ? (
									<>
										<p>{formatPrice(calculateGoodsVatAmount(goods, selection), locale)}</p>
										<p className="text-xs text-leo-divider-gray">{goods.tax_rate.toLocaleString(locale)}%</p>
									</>
								) : ""}
							</td>
							<td className="py-3 text-right">{formatPrice(calculateGoodsTotal(goods, selection), locale)}</td>
							<td className="py-3 text-right">
								<button type="button" className="font-bold text-leo-dark-brown" onClick={() => onRemove(goods.id)}>{t("components.goodsSelector.table.remove")}</button>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

function normalizeSelectionDateRange(selection: GoodsSelection) {
	if (selection.date_from && selection.date_to && selection.date_to < selection.date_from) {
		return {...selection, date_to: selection.date_from};
	}
	return selection;
}

function constrainSelectionDateRange(selection: GoodsSelection, minDate?: string, maxDate?: string) {
	const next = {...selection};

	if (minDate && next.date_from && next.date_from < minDate) {
		next.date_from = minDate;
	}
	if (maxDate && next.date_from && next.date_from > maxDate) {
		next.date_from = maxDate;
	}
	if (minDate && next.date_to && next.date_to < minDate) {
		next.date_to = minDate;
	}
	if (maxDate && next.date_to && next.date_to > maxDate) {
		next.date_to = maxDate;
	}

	return normalizeSelectionDateRange(next);
}
